import React from 'react';

import { NextPage } from 'next';

import { HomePageProps } from '@domains/view';

import { useCommonData } from '@stores/global-data';

import { apiSurveyComplete, apiSurveyDismiss } from '@api/methods';

import { isNotEmpty, isValueSet } from '@utils/guards';
import { SnackBar } from '@utils/snackbar';

import { DefaultLayout } from '@layouts/DefaultLayout/DefaultLayout';

import { ArticlesSlider } from '@components/article/ArticlesSlider/ArticlesSlider';
import { CategoriesGrid } from '@components/catalog/CategoriesGrid/CategoriesGrid';
import { BusinessBlock } from '@components/common/BusinessBlock/BusinessBlock';
import { Container } from '@components/common/Container/Container';
import { QuizBlock } from '@components/common/QuizBlock/QuizBlock';
import { RecipesSlider } from '@components/common/RecipesSlider/RecipesSlider';
import { HomeSchema } from '@components/common/SchemaOrg/HomeSchema';
import { FeatureSlider } from '@components/home/FeatureSlider/FeatureSlider';
import { HeroSlider } from '@components/home/HeroSlider/HeroSlider';
import { HomeAdvantages } from '@components/home/HomeAdvantages/HomeAdvantages';
import { LastOrder } from '@components/orders-history/LastOrder/LastOrder';
import { ProductsSlider } from '@components/product/ProductsSlider/ProductsSlider';

import styles from './Home.module.scss';

export const Home: NextPage<HomePageProps> = (props) => {
    const {
        data: {
            for_business: {
                title: forBusinessTitle,
                subtitle: forBusinessSubtitle,
                button_text: forBusinessText,
                button_url: forBusinessUrl,
            },
            news,
            slides,
            advantages,
            featured_block,
            categories,
            recipes,
            products,
            latest_order,
            seo,
            is_novelty_products_available,
            is_promo_products_available,
        },
    } = props;

    const {
        settings: { show_survey_banner, survey_form_url },
    } = useCommonData();

    const hasFeatureSlider =
        featured_block.length !== 0 || is_novelty_products_available || is_promo_products_available;

    return (
        <DefaultLayout {...seo}>
            <HomeSchema />
            {isNotEmpty(slides) && <HeroSlider slides={slides} />}
            {hasFeatureSlider && (
                <FeatureSlider
                    slides={featured_block.slice(0, 1)}
                    hasNoveltyProductsCard={is_novelty_products_available}
                    hasPromoProductsCard={is_promo_products_available}
                />
            )}
            {isValueSet(latest_order) && isNotEmpty(latest_order.several_items) && (
                <LastOrder {...latest_order} />
            )}

            {isNotEmpty(categories) && (
                <CategoriesGrid categories={categories} className={styles.categoryGrid} />
            )}
            {isNotEmpty(advantages) && <HomeAdvantages advantages={advantages} />}
            {isNotEmpty(news) && (
                <ArticlesSlider articles={news} className={styles.articlesSlider} />
            )}
            {isNotEmpty(products) && <ProductsSlider products={products} />}
            <BusinessBlock
                title={forBusinessTitle}
                subtitle={forBusinessSubtitle}
                button_text={forBusinessText}
                button_url={forBusinessUrl}
            />
            {isNotEmpty(recipes.items) && (
                <RecipesSlider
                    recipes={recipes.items}
                    rubrics={recipes.rubrics}
                    title="Рецепты"
                    isWithTitleLink
                    isWithChips
                    isWithPagination={false}
                />
            )}

            {show_survey_banner && survey_form_url !== null && (
                <div className={styles.quizWrapper}>
                    <Container classes={{ root: styles.quizContainer }}>
                        <QuizBlock
                            isSmall
                            className={styles.quizBlock}
                            textWrapperClassName={styles.quizBlockTextWrapper}
                            link={survey_form_url}
                            heading="Как прошли ваши заказы?"
                            description={
                                <>
                                    Поделитесь впечатлениями
                                    <br />
                                    о&nbsp;качестве нашего обслуживания
                                </>
                            }
                            onComplete={() => apiSurveyComplete({}).catch(SnackBar.handleApiError)}
                            onDismiss={() => apiSurveyDismiss({}).catch(SnackBar.handleApiError)}
                        />
                    </Container>
                </div>
            )}
        </DefaultLayout>
    );
};
